/**
 * Vertragsbaukasten, Datenmodell (Schema 1): Eine Vorlage ist eine geordnete Liste von Bausteinen (contract_templates.blocks,
 * Migration 0112). Konzept: pruefung/saas-tauglichkeit/bericht-vertraege.md, Teil 3.3.
 *
 * Die Bausteine liegen VOR dem Renderer: contract-blocks-compile.ts übersetzt sie in genau das Vorlagen-HTML mit Platzhaltern,
 * das heute in body_html, annex_html, annex_hidden_from, variables und required_signers steht. Renderer, Prüfung, Versand,
 * Unterschrift, Sichtlogik und Beweiskette bleiben unverändert. contract-blocks-convert.ts wandelt den Bestand (HTML) zurück in
 * Bausteine; der Rundlauf ist für alle Mustervorlagen zeichengenau (packages/domain/test/contract-blocks.test.ts).
 *
 * Schema 1 enthält bewusst nur, was ohne Renderer-Erweiterung geht: keine automatische Nummerierung, keine Verweise, keine
 * Tabellenzeilen aus den Beiträgen, kein „ist gleich“ in Bedingungen. Das kommt mit einer späteren Schemaversion.
 *
 * Leerraum: `ws` (vor einem Baustein), `wsEnd`, `wsElse` und `sep` halten nur den Leerraum zwischen den Teilen fest, damit
 * umgewandelte Bestandsvorlagen Zeichen für Zeichen denselben Text (und damit denselben Prüfwert) ergeben. Neue Bausteine
 * lassen sie weg; dann gilt der Standard aus DEFAULT_WS.
 *
 * validateBlocks prüft das Schema mit denselben Regeln und in derselben Reihenfolge wie app.contract_blocks_problem (0112);
 * der Gleichlauf-Test steht in tests/db/99zzzzzz_vertragsbaukasten.test.ts.
 */
import { CONTRACT_LIMITS } from "./contract-markup.js";
import { CONTRACT_ROLES, type ContractRole } from "./contract-variables.js";

export const BLOCKS_SCHEMA_VERSION = 1;

export type InlineMark = "b" | "i" | "u";
export const INLINE_MARKS: readonly InlineMark[] = ["b", "i", "u"];

/** Feld oder Bedingung: Systemwert (`deal.budget`) oder eigene Angabe (`publish_deadline`, im Text `var.publish_deadline`). */
export interface FieldRef {
  src: "system" | "custom";
  path: string;
}

export interface I18nText {
  de: string;
  en: string;
}

/** Text im Baustein: Läufe mit Auszeichnung, Feld-Chips, Zeilenumbrüche und „Nur wenn“ im Satz. */
export type Inline =
  | { t: "text"; v: string; marks?: InlineMark[] }
  | { t: "br"; marks?: InlineMark[] }
  | { t: "field"; field: FieldRef; filters?: string[]; marks?: InlineMark[] }
  | { t: "if"; when: FieldRef; then: Inline[]; else?: Inline[] };

export type BlockAlign = "center" | "right";

export interface ListItem {
  id: string;
  text: Inline[];
  /** Punkt erscheint nur, wenn die Bedingung zutrifft */
  when?: FieldRef;
  ws?: string;
}

interface BlockBase {
  id: string;
  /** Leerraum vor dem Baustein (nur Rundlauftreue) */
  ws?: string;
}

export interface HeadingBlock extends BlockBase { type: "heading"; level: 1 | 2 | 3 | 4; text: Inline[]; align?: BlockAlign }
export interface ParagraphBlock extends BlockBase { type: "paragraph"; text: Inline[]; align?: BlockAlign }
/** Interner Hinweis: nur im Editor, nie im Vertrag (<p class="tk-sample-notice">, der Renderer entfernt ihn) */
export interface NoteBlock extends BlockBase { type: "note"; text: Inline[] }
export interface ListBlock extends BlockBase { type: "list"; ordered: boolean; items: ListItem[]; wsEnd?: string }
/** Tabelle mit fester Kopfzeile und festen Zeilen (Zeilen aus den Beiträgen erst mit Renderer-Erweiterung) */
export interface TableBlock extends BlockBase { type: "table"; header?: Inline[][]; rows: Inline[][][] }
/** Eine Zeile „Bezeichnung: Wert“ */
export interface FieldBlock extends BlockBase { type: "field"; label: string; field: FieldRef; filters?: string[] }
/** Nur wenn …, optional mit Sonst */
export interface ConditionBlock extends BlockBase {
  type: "condition"; when: FieldRef; children: Block[]; otherwise?: Block[]; wsElse?: string; wsEnd?: string;
}
/** Wählbare Klausel: beim Erstellen an- oder abwählbar; ergibt eine Ja/Nein-Angabe mit ui = "choice" */
export interface ChoiceBlock extends BlockBase {
  type: "choice"; key: string; question: I18nText; help?: I18nText; default: boolean; children: Block[]; wsEnd?: string;
}
/** Vertraulicher Teil (Stufe 1: höchstens einer, oberste Ebene; wird zu annex_html, annex_hidden_from = übrige Rollen) */
export interface PrivateBlock extends BlockBase {
  type: "private"; visibleTo: ContractRole[]; title?: string; children: Block[]; wsEnd?: string;
}
/** Anlage für alle Parteien, als Abschnitt „Anlage n: Titel“ an ihrer Stelle im Hauptteil (vor den Unterschriften) */
export interface AttachmentBlock extends BlockBase { type: "attachment"; title: string; children: Block[]; wsEnd?: string }
/** Klausel aus der Bibliothek: eingefrorener Schnappschuss */
export interface ClauseBlock extends BlockBase { type: "clause"; clauseId: string; version: number; children: Block[] }
/** Unterschriftsfelder; required_signers ergibt sich aus allen Unterschriften der Vorlage */
export interface SignaturesBlock extends BlockBase { type: "signatures"; parties: ContractRole[]; sep?: string }
export interface PageBreakBlock extends BlockBase { type: "pagebreak" }
/** Freitext aus alter Vorlage: was sich beim Umwandeln nicht zuordnen ließ, unverändert */
export interface LegacyHtmlBlock extends BlockBase { type: "legacy_html"; html: string }

export type Block =
  | HeadingBlock | ParagraphBlock | NoteBlock | ListBlock | TableBlock | FieldBlock | ConditionBlock | ChoiceBlock | PrivateBlock
  | AttachmentBlock | ClauseBlock | SignaturesBlock | PageBreakBlock | LegacyHtmlBlock;

export type BlockType = Block["type"];

export const BLOCK_TYPES: readonly BlockType[] = [
  "heading", "paragraph", "note", "list", "table", "field", "condition", "choice", "private", "attachment", "clause", "signatures", "pagebreak", "legacy_html",
];

/** Erlaubte Schlüssel je Baustein (außer id, type, ws); gleich in app.contract_blocks_problem. */
export const BLOCK_KEYS: Readonly<Record<BlockType, readonly string[]>> = {
  heading: ["level", "text", "align"],
  paragraph: ["text", "align"],
  note: ["text"],
  list: ["ordered", "items", "wsEnd"],
  table: ["header", "rows"],
  field: ["label", "field", "filters"],
  condition: ["when", "children", "otherwise", "wsElse", "wsEnd"],
  choice: ["key", "question", "help", "default", "children", "wsEnd"],
  private: ["visibleTo", "title", "children", "wsEnd"],
  attachment: ["title", "children", "wsEnd"],
  clause: ["clauseId", "version", "children"],
  signatures: ["parties", "sep"],
  pagebreak: [],
  legacy_html: ["html"],
};

/** Standard-Leerraum, wenn ein Baustein keinen eigenen trägt. */
export type ContainerKind = "body" | "annex" | "cond" | "attachment" | "clause";
export const DEFAULT_WS = {
  first: { body: "", annex: "", cond: "\n", attachment: "\n\n", clause: "" } as Record<ContainerKind, string>,
  next: "\n\n",
  end: { annex: "", cond: "\n", attachment: "" } as Record<"annex" | "cond" | "attachment", string>,
  else: "\n",
  item: "\n  ",
  listEnd: "\n",
  sep: "\n",
} as const;

export function defaultWs(kind: ContainerKind, index: number): string {
  return index === 0 ? DEFAULT_WS.first[kind] : DEFAULT_WS.next;
}

// ---------------------------------------------------------------------------
// Schema-Prüfung (gleich app.contract_blocks_problem)
// ---------------------------------------------------------------------------

export const BLOCK_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
const WS_RX = new RegExp(`^[ \\t\\r\\n]{0,${CONTRACT_LIMITS.ws}}$`);
const KEY_RX = /^[a-z][a-z0-9_]{0,63}$/;
const SYSTEM_PATH_RX = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
const FILTER_RX = /^[a-z]{1,20}$/;
const CLAUSE_ID_RX = /^[A-Za-z0-9_-]{1,64}$/;
const INLINE_TYPES = ["text", "br", "field", "if"] as const;
const INLINE_KEYS: Record<(typeof INLINE_TYPES)[number], readonly string[]> = {
  text: ["t", "v", "marks"], br: ["t", "marks"], field: ["t", "field", "filters", "marks"], if: ["t", "when", "then", "else"],
};

/** Länge in Zeichen (Codepunkte), wie length() in Postgres. */
export function codePoints(s: string): number {
  let n = s.length;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) { n -= 1; i += 1; }
    }
  }
  return n;
}

/** Leerraum am Rand entfernen (nur Leerzeichen, Tab, Zeilenumbrüche; gleich btrim in der Datenbank). */
export function trimAscii(s: string): string {
  return s.replace(/^[ \t\r\n\f\v]+|[ \t\r\n\f\v]+$/g, "");
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === "string";

function unknownKey(o: Record<string, unknown>, allowed: readonly string[]): string | null {
  const extra = Object.keys(o).filter((k) => !allowed.includes(k)).sort();
  return extra[0] ?? null;
}

function fieldRefOk(v: unknown): boolean {
  if (!isObj(v) || unknownKey(v, ["src", "path"]) !== null) return false;
  if (v.src === "system") return isStr(v.path) && SYSTEM_PATH_RX.test(v.path);
  if (v.src === "custom") return isStr(v.path) && KEY_RX.test(v.path);
  return false;
}

function marksOk(v: unknown): boolean {
  if (v === undefined) return true;
  return Array.isArray(v) && v.length <= INLINE_MARKS.length && v.every((m) => INLINE_MARKS.includes(m as InlineMark)) && new Set(v).size === v.length;
}

function filtersOk(v: unknown): boolean {
  if (v === undefined) return true;
  return Array.isArray(v) && v.length <= 5 && v.every((f) => isStr(f) && FILTER_RX.test(f));
}

function wsOk(v: unknown): boolean {
  return v === undefined || (isStr(v) && WS_RX.test(v));
}

function i18nOk(v: unknown, max: number, deRequired: boolean): boolean {
  if (!isObj(v) || unknownKey(v, ["de", "en"]) !== null) return false;
  if (!isStr(v.de) || codePoints(v.de) > max || (deRequired && trimAscii(v.de) === "")) return false;
  return v.en === undefined || (isStr(v.en) && codePoints(v.en) <= max);
}

function rolesOk(v: unknown, requireAgency: boolean): boolean {
  return Array.isArray(v) && v.length >= 1 && v.every((r) => (CONTRACT_ROLES as readonly unknown[]).includes(r)) && new Set(v).size === v.length
    && (!requireAgency || v.includes("agency"));
}

interface ValidationState { count: number; ids: Set<string>; privates: number; text: number }

function checkInline(list: unknown, depth: number, owner: string, st: ValidationState): string | null {
  if (!Array.isArray(list)) return `inline:not_array@${owner}`;
  if (depth > CONTRACT_LIMITS.depth) return `inline:too_deep@${owner}`;
  for (const n of list) {
    if (!isObj(n) || !INLINE_TYPES.includes(n.t as never)) return `inline:type@${owner}`;
    const t = n.t as (typeof INLINE_TYPES)[number];
    const extra = unknownKey(n, INLINE_KEYS[t]);
    if (extra) return `inline:unknown_key:${extra}@${owner}`;
    if (t !== "if" && !marksOk(n.marks)) return `inline:marks@${owner}`;
    if (t === "text") {
      if (!isStr(n.v) || n.v === "") return `inline:text@${owner}`;
      st.text += codePoints(n.v);
    } else if (t === "field") {
      if (!fieldRefOk(n.field)) return `inline:field@${owner}`;
      if (!filtersOk(n.filters)) return `inline:filters@${owner}`;
    } else if (t === "if") {
      if (!fieldRefOk(n.when)) return `inline:when@${owner}`;
      const p = checkInline(n.then, depth + 1, owner, st) ?? (n.else === undefined ? null : checkInline(n.else, depth + 1, owner, st));
      if (p) return p;
    }
  }
  return null;
}

function checkList(list: unknown, depth: number, top: boolean, inPrivate: boolean, st: ValidationState, owner: string): string | null {
  if (!Array.isArray(list)) return `block:children@${owner}`;
  if (depth > CONTRACT_LIMITS.depth) return `blocks:too_deep@${owner}`;
  for (const b of list) {
    const p = checkBlock(b, depth, top, inPrivate, st);
    if (p) return p;
  }
  return null;
}

function checkBlock(b: unknown, depth: number, top: boolean, inPrivate: boolean, st: ValidationState): string | null {
  st.count += 1;
  if (st.count > CONTRACT_LIMITS.blocks) return "blocks:too_many";
  if (!isObj(b)) return "block:not_object";
  if (!isStr(b.id) || !BLOCK_ID_PATTERN.test(b.id)) return "block:id";
  const id = b.id;
  if (st.ids.has(id)) return `block:duplicate_id@${id}`;
  st.ids.add(id);
  if (!BLOCK_TYPES.includes(b.type as BlockType)) return `block:type@${id}`;
  const type = b.type as BlockType;
  const extra = unknownKey(b, ["id", "type", "ws", ...BLOCK_KEYS[type]]);
  if (extra) return `block:unknown_key:${extra}@${id}`;
  if (!wsOk(b.ws)) return `block:ws@${id}`;
  st.text = 0;
  const text = (): string | null => (st.text > CONTRACT_LIMITS.textChars ? `block:text_too_long@${id}` : null);
  const align = () => b.align === undefined || b.align === "center" || b.align === "right";
  switch (type) {
    case "heading":
      if (![1, 2, 3, 4].includes(b.level as number)) return `block:level@${id}`;
      if (!align()) return `block:align@${id}`;
      return checkInline(b.text, 1, id, st) ?? text();
    case "paragraph":
      if (!align()) return `block:align@${id}`;
      return checkInline(b.text, 1, id, st) ?? text();
    case "note":
      return checkInline(b.text, 1, id, st) ?? text();
    case "list": {
      if (typeof b.ordered !== "boolean") return `block:ordered@${id}`;
      if (!Array.isArray(b.items) || b.items.length > CONTRACT_LIMITS.items) return `block:items@${id}`;
      for (const it of b.items) {
        if (!isObj(it) || !isStr(it.id) || !BLOCK_ID_PATTERN.test(it.id)) return `item:id@${id}`;
        if (st.ids.has(it.id)) return `block:duplicate_id@${it.id}`;
        st.ids.add(it.id);
        const x = unknownKey(it, ["id", "text", "when", "ws"]);
        if (x) return `item:unknown_key:${x}@${it.id}`;
        if (!wsOk(it.ws)) return `item:ws@${it.id}`;
        if (it.when !== undefined && !fieldRefOk(it.when)) return `item:when@${it.id}`;
        const p = checkInline(it.text, 1, it.id, st);
        if (p) return p;
      }
      if (!wsOk(b.wsEnd)) return `block:ws@${id}`;
      return text();
    }
    case "table": {
      const row = (r: unknown) => Array.isArray(r) && r.length >= 1 && r.length <= CONTRACT_LIMITS.cells;
      if (b.header !== undefined && !row(b.header)) return `block:header@${id}`;
      if (!Array.isArray(b.rows) || b.rows.length > CONTRACT_LIMITS.items || !b.rows.every(row)) return `block:rows@${id}`;
      for (const cell of [...((b.header as unknown[] | undefined) ?? []), ...(b.rows as unknown[][]).flat()]) {
        const p = checkInline(cell, 1, id, st);
        if (p) return p;
      }
      return text();
    }
    case "field":
      if (!isStr(b.label) || trimAscii(b.label) === "" || codePoints(b.label) > 120) return `block:label@${id}`;
      if (!fieldRefOk(b.field)) return `block:field@${id}`;
      if (!filtersOk(b.filters)) return `block:filters@${id}`;
      return null;
    case "condition":
      if (!fieldRefOk(b.when)) return `block:when@${id}`;
      if (!wsOk(b.wsElse) || !wsOk(b.wsEnd)) return `block:ws@${id}`;
      return checkList(b.children, depth + 1, false, inPrivate, st, id) ?? (b.otherwise === undefined ? null : checkList(b.otherwise, depth + 1, false, inPrivate, st, id));
    case "choice":
      if (!isStr(b.key) || !KEY_RX.test(b.key)) return `block:key@${id}`;
      if (!i18nOk(b.question, 300, true)) return `block:question@${id}`;
      if (b.help !== undefined && !i18nOk(b.help, 1000, false)) return `block:help@${id}`;
      if (typeof b.default !== "boolean") return `block:default@${id}`;
      if (!wsOk(b.wsEnd)) return `block:ws@${id}`;
      return checkList(b.children, depth + 1, false, inPrivate, st, id);
    case "private":
      if (!top) return `block:private_position@${id}`;
      st.privates += 1;
      if (st.privates > 1) return `block:private_count@${id}`;
      if (!rolesOk(b.visibleTo, true)) return `block:visible_to@${id}`;
      if (b.title !== undefined && (!isStr(b.title) || codePoints(b.title) > 120)) return `block:title@${id}`;
      if (!wsOk(b.wsEnd)) return `block:ws@${id}`;
      return checkList(b.children, depth + 1, false, true, st, id);
    case "attachment":
      if (!isStr(b.title) || trimAscii(b.title) === "" || codePoints(b.title) > 120) return `block:title@${id}`;
      if (!wsOk(b.wsEnd)) return `block:ws@${id}`;
      return checkList(b.children, depth + 1, false, inPrivate, st, id);
    case "clause":
      if (!isStr(b.clauseId) || !CLAUSE_ID_RX.test(b.clauseId)) return `block:clause@${id}`;
      if (typeof b.version !== "number" || !Number.isInteger(b.version) || b.version < 1) return `block:version@${id}`;
      return checkList(b.children, depth + 1, false, inPrivate, st, id);
    case "signatures":
      if (inPrivate) return `block:signature_in_private@${id}`;
      if (!rolesOk(b.parties, false)) return `block:parties@${id}`;
      if (!wsOk(b.sep)) return `block:ws@${id}`;
      return null;
    case "pagebreak":
      return null;
    case "legacy_html":
      if (!isStr(b.html) || codePoints(b.html) > CONTRACT_LIMITS.htmlChars) return `block:html@${id}`;
      return null;
  }
}

/**
 * Erster Verstoß gegen das Schema oder null. Kennungen wie `block:type@b3`, `inline:field@b7`, `blocks:too_many`
 * (gleich app.contract_blocks_problem). Prüft auch Grenzen (CONTRACT_LIMITS), eindeutige Kennungen, höchstens einen
 * vertraulichen Teil auf oberster Ebene und keine Unterschriften darin.
 */
export function validateBlocks(doc: unknown): string | null {
  if (!Array.isArray(doc)) return "blocks:not_array";
  const st: ValidationState = { count: 0, ids: new Set(), privates: 0, text: 0 };
  return checkList(doc, 1, true, false, st, "doc");
}

// ---------------------------------------------------------------------------
// Hilfen für Editor und Prüfung
// ---------------------------------------------------------------------------

/** Pfad im Platzhalter: `deal.budget` bzw. `var.key`. */
export function fieldPath(f: FieldRef): string {
  return f.src === "custom" ? `var.${f.path}` : f.path;
}

/** FieldRef aus einem Platzhalterpfad (`var.x` → eigene Angabe). */
export function fieldRefOf(path: string): FieldRef {
  return path.startsWith("var.") ? { src: "custom", path: path.slice(4) } : { src: "system", path };
}

/** Alle Bausteine in Dokumentreihenfolge (Tiefensuche), mit Eltern-Kette. */
export function* walkBlocks(blocks: readonly Block[], parents: readonly Block[] = []): Generator<{ block: Block; parents: readonly Block[] }> {
  for (const b of blocks) {
    yield { block: b, parents };
    const kids = childLists(b);
    for (const list of kids) yield* walkBlocks(list, [...parents, b]);
  }
}

/** Kindlisten eines Bausteins (Sonst-Zweig einer Bedingung als zweite Liste). */
export function childLists(b: Block): Block[][] {
  switch (b.type) {
    case "condition": return b.otherwise ? [b.children, b.otherwise] : [b.children];
    case "choice": case "private": case "attachment": case "clause": return [b.children];
    default: return [];
  }
}
